/* ============================================================
   Mistiq — Motion layer (behaviour)

   Loaded deferred from snippets/mistiq-animations.liquid. The tiny
   bootstrap in that snippet runs inline in <head> and does two things
   this file relies on:

     1. adds .mq-motion to <html>, which is what gates every hidden
        state in mistiq-motion.css;
     2. tags reveal candidates with data-reveal as the document is
        parsed, before first paint, so nothing ever flashes in at full
        opacity and then hides itself.

   It also arms a failsafe timer. If this file never arrives, that timer
   adds .mq-motion-off and the page renders as plain static content.
   Clearing that timer is the first thing this file does.

   Three behaviours live here and nothing else:
     - scroll reveal
     - the price / availability cross-fade on variant change
     - lazy image fade on decode

   No libraries, no CDN, no build step.
   ============================================================ */

(function () {
  'use strict';

  var win = window;
  var doc = document;

  var boot = win.MistiqMotion || {};
  var reduced = win.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* The bootstrap's failsafe is no longer needed. */
  if (boot.failsafe) clearTimeout(boot.failsafe);

  var PROCESSED = 'data-mq-processed';
  var STEP = 90; /* keep in sync with --reveal-step */
  var MAX_STEPS = 6; /* cascade cap — see setupGroup */

  /* Containers whose direct children should cascade rather than each
     animating off its own trigger. */
  var GROUPS = [
    '.footer-content',
    '.product-grid',
    '.resource-list--grid',
    '.editorial-product__grid',
    '.editorial-blog__grid',
    '.editorial-collection__grid'
  ].join(',');

  /* ----------------------------------------------------------
     Scroll reveal
     ---------------------------------------------------------- */

  var observer = null;

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var el = entries[i].target;
          observer.unobserve(el); /* reveal once, never again */
          reveal(el);
        }
      },
      {
        /* Start a little before the element is properly in view, so the
           motion is already underway by the time it is being read. */
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.15
      }
    );

    return observer;
  }

  function reveal(el) {
    /* A group trigger reveals its children; the group box itself is
       only ever a tripwire. */
    var children = el.mqRevealChildren;

    if (children) {
      for (var i = 0; i < children.length; i++) settle(children[i]);
      return;
    }

    settle(el);
  }

  function settle(el) {
    el.classList.add('mq-revealed');

    el.addEventListener(
      'transitionend',
      function () {
        el.classList.add('mq-reveal-done');
      },
      { once: true }
    );
  }

  /* Visible without the customer having scrolled at all. Land it now,
     with no transition — the brief is explicit that nothing above the
     fold on load may animate. */
  function isOnScreenNow(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return r.top < win.innerHeight && r.bottom > 0;
  }

  function land(el) {
    el.classList.add('mq-reveal-instant', 'mq-reveal-done');
  }

  function setDelay(el, index) {
    el.style.setProperty('--reveal-delay', Math.min(index, MAX_STEPS) * STEP + 'ms');
  }

  function setupReveal(scope) {
    var targets = scope.querySelectorAll('[data-reveal]:not([' + PROCESSED + '])');
    if (!targets.length) return;

    var noObserver = !('IntersectionObserver' in win);
    var groups = [];

    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      el.setAttribute(PROCESSED, '');

      if (reduced || noObserver) {
        land(el);
        continue;
      }

      var group = el.parentElement && el.parentElement.closest(GROUPS);

      /* Only treat it as a cascade member if it is a direct child of the
         group — nested content keeps its own trigger. */
      if (group && el.parentElement === group) {
        if (groups.indexOf(group) === -1) groups.push(group);
        continue;
      }

      if (isOnScreenNow(el)) land(el);
      else getObserver().observe(el);
    }

    for (var g = 0; g < groups.length; g++) setupGroup(groups[g]);
  }

  function setupGroup(group) {
    var children = [];
    var kids = group.children;

    for (var i = 0; i < kids.length; i++) {
      if (!kids[i].hasAttribute('data-reveal')) continue;
      /* Already landed by the bootstrap's on-screen sweep. It is not part
         of the cascade and must not be re-hidden. */
      if (kids[i].classList.contains('mq-reveal-instant')) continue;
      children.push(kids[i]);
    }

    if (!children.length) return;

    /* A group taller than most of the viewport — a 24-card collection
       grid, or any grid collapsed to one column on a phone — must not
       cascade off a single trigger, or its lower children finish
       animating while still far below the fold.

       Instead, each child gets its own trigger and a delay based on its
       position within its own visual row. Every row then cascades left
       to right as it arrives, and a single-column mobile layout — where
       every child is its own row — correctly gets no delay at all.

       Rows are measured, not breakpointed, so this self-corrects on
       rotation and on any column count without hardcoding a width. */
    var tall = group.offsetHeight > win.innerHeight * 0.8;

    if (tall) {
      var rowTop = null;
      var inRow = 0;

      for (var t = 0; t < children.length; t++) {
        var kid = children[t];
        var top = Math.round(kid.getBoundingClientRect().top);

        if (rowTop === null || Math.abs(top - rowTop) > 4) {
          rowTop = top;
          inRow = 0;
        } else {
          inRow++;
        }

        setDelay(kid, inRow);

        if (isOnScreenNow(kid)) land(kid);
        else getObserver().observe(kid);
      }

      return;
    }

    /* Short enough to take in at once: one trigger, children cascade
       together. Cap the cascade — past six steps the last item waits
       long enough that it reads as broken rather than considered. */
    for (var c = 0; c < children.length; c++) setDelay(children[c], c);

    group.mqRevealChildren = children;
    getObserver().observe(group);
  }

  /* ----------------------------------------------------------
     Variant change — price and availability cross-fade

     Deliberately driven off the customer's own input event rather than
     Horizon's internal product-select event, so it cannot silently stop
     working when the theme's event names change. The dip starts on
     change; it ends when the target's own DOM actually mutates, with a
     hard ceiling so a failed fetch can never leave the price dimmed.
     ---------------------------------------------------------- */

  var SWAP_TARGETS = 'product-price, product-inventory, product-sku';
  var SWAP_CEILING = 1500;

  function crossFade(el) {
    if (el.mqSwapTimer) clearTimeout(el.mqSwapTimer);
    if (el.mqSwapWatcher) el.mqSwapWatcher.disconnect();

    el.classList.add('mq-swapping');

    var restore = function () {
      if (el.mqSwapTimer) clearTimeout(el.mqSwapTimer);
      if (el.mqSwapWatcher) el.mqSwapWatcher.disconnect();
      el.mqSwapTimer = null;
      el.mqSwapWatcher = null;
      el.classList.remove('mq-swapping');
    };

    /* The content has been replaced. Give the browser one frame to lay
       the new node out at the dipped opacity, then fade it up — without
       that frame the transition has no start value to run from. */
    var watcher = new MutationObserver(function () {
      win.requestAnimationFrame(function () {
        win.requestAnimationFrame(restore);
      });
    });

    watcher.observe(el, { childList: true, subtree: true, characterData: true });

    el.mqSwapWatcher = watcher;

    /* Backstop. In a background tab requestAnimationFrame never runs, so
       this is the path that actually clears the class there. */
    el.mqSwapTimer = setTimeout(restore, SWAP_CEILING);
  }

  function onVariantChange(event) {
    if (reduced) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var picker = target.closest('variant-picker');
    if (!picker) return;

    /* Card and quick-add pickers re-render their own compact markup and
       are not what this fade is for. */
    if (picker.closest('product-card')) return;

    var scope = picker.closest('.shopify-section, dialog') || doc;
    var els = scope.querySelectorAll(SWAP_TARGETS);

    for (var i = 0; i < els.length; i++) {
      if (els[i].closest('product-card')) continue;
      crossFade(els[i]);
    }
  }

  /* Capture, so the dip is applied before Horizon's own listeners begin
     fetching the replacement markup. */
  doc.addEventListener('change', onVariantChange, true);

  /* ----------------------------------------------------------
     Lazy image fade on decode

     Only loading="lazy" images. Eager and LCP images are left entirely
     alone: an opacity-0 hero delays the paint that Largest Contentful
     Paint measures, which would trade a real metric for a small effect.
     width and height attributes are never touched, so the reserved box
     is unchanged and no layout shift is introduced.
     ---------------------------------------------------------- */

  function fadeImages(scope) {
    if (reduced) return;

    var imgs = scope.querySelectorAll('img[loading="lazy"]:not([data-mq-fade])');

    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        /* Already decoded and painted. Fading it now would be a flash,
           not an arrival. */
        if (img.complete && img.naturalWidth > 0) return;

        img.setAttribute('data-mq-fade', '');

        var shown = false;
        var show = function () {
          if (shown) return;
          shown = true;
          img.classList.add('mq-img-ready');
        };

        if (typeof img.decode === 'function') {
          img.decode().then(show).catch(show);
        } else {
          img.addEventListener('load', show, { once: true });
          img.addEventListener('error', show, { once: true });
        }

        /* An image is never left invisible, whatever decode does. */
        setTimeout(show, 3000);
      })(imgs[i]);
    }
  }

  /* ----------------------------------------------------------
     Wiring
     ---------------------------------------------------------- */

  function process(scope) {
    var target = scope && scope.querySelectorAll ? scope : doc;
    setupReveal(target);
    fadeImages(target);
  }

  function init() {
    process(doc);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Horizon swaps content in on filtering, pagination and quick add, and
     the theme editor rebuilds sections. Pick up anything new. The
     bootstrap's parse-time tagger keeps running for the life of the
     page, so new nodes arrive already carrying data-reveal. */
  var incoming = null;

  function schedule() {
    if (incoming) return;
    incoming = win.setTimeout(function () {
      incoming = null;
      init();
    }, 50);
  }

  var mutations = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].addedNodes.length) {
        schedule();
        return;
      }
    }
  });

  function watchMain() {
    var main = doc.getElementById('MainContent');
    if (main) mutations.observe(main, { childList: true, subtree: true });

    var footer = doc.querySelector('footer');
    if (footer) mutations.observe(footer, { childList: true, subtree: true });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', watchMain);
  } else {
    watchMain();
  }

  doc.addEventListener('shopify:section:load', function (event) {
    process(event.target || doc);
  });
})();
